import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Enclave } from 'types';

import { EnclaveMetadataObject, UserObject } from '@api/types';
import { AdminTeamObject } from '@components/features/Admin/types';
import { TeamActivityResponse } from '@components/features/Settings/TeamActivity/queries';
import { api } from '@services/apiService';
import { adminTeamEnclaveInfo } from '@utils/resources/admin';

export function useTeam() {
  const { teamUuid } = useParams();

  return useQuery({
    suspense: true,
    queryKey: ['admin', 'teams', teamUuid],
    queryFn: async () =>
      api
        .get(`teams/${teamUuid}`, {
          headers: {
            accept: 'application/json;version=2',
          },
        })
        .json<{ team: AdminTeamObject }>(),
    enabled: !!teamUuid,
  });
}

export interface UseTeamActivityParams {
  appUuid?: string;
}

export function useTeamActivity({ appUuid }: UseTeamActivityParams = {}) {
  const { teamUuid } = useParams();

  return useInfiniteQuery({
    queryKey: ['teamActivity', teamUuid],
    queryFn: async ({ pageParam }) =>
      api
        .get<TeamActivityResponse>(`teams/${teamUuid}/activity/audit-logs`, {
          searchParams: {
            from: pageParam || 0,
          },
        })
        .json(),
    getNextPageParam: (lastPage) => {
      const nextPossibleParam =
        Number(lastPage.from) + Number(lastPage.pageSize);
      return nextPossibleParam < lastPage.totalDocumentSize
        ? nextPossibleParam
        : undefined;
    },
    select: !appUuid
      ? undefined
      : (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            sortedLogs: page.sortedLogs.filter(
              (log) => log.evervault.app?.uuid === appUuid
            ),
          })),
        }),
  });
}

export function useUser() {
  const { userId } = useParams();

  return useQuery({
    suspense: true,
    queryKey: ['admin', 'users', userId],
    queryFn: async () => api.get(`admin/users/${userId}`).json<UserObject>(),
    enabled: !!userId,
  });
}

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      newRoleName,
    }: {
      userId: number;
      newRoleName: string;
    }) =>
      api
        .patch(`admin/users/${userId}`, {
          json: { newRoleName },
        })
        .json(),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'users', variables.userId.toString()],
      });
    },
  });
};

export function useProductMetrics() {
  return useQuery({
    queryKey: ['product-metrics'],
    queryFn: async () => await api.get('admin/product-metrics').json(),
  });
}

export function useEnclave() {
  return useSuspenseQuery<EnclaveMetadataObject>({
    queryKey: ['admin', 'enclaves', 'metadata'],
    queryFn: async () => api.get('admin/teams/enclaves-metadata').json(),
  });
}

export type AdminEnclave = Pick<
  Enclave,
  'uuid' | 'name' | 'appUuid' | 'disabled' | 'disabledReason'
> & {
  enclaveScalingConfig?: {
    desiredReplicas: number;
    minReplicas: number;
    maxReplicas: number;
  };
};

export const teamEnclavesQueryKey = (teamUuid: string) => [
  'admin',
  'teams',
  teamUuid,
  'enclaves',
];

export function useTeamEnclaves(teamUuid: string) {
  return useQuery({
    queryKey: teamEnclavesQueryKey(teamUuid),
    queryFn: () =>
      api
        .get(adminTeamEnclaveInfo(teamUuid))
        .json<{ enclaves?: AdminEnclave[] }>(),
    enabled: !!teamUuid,
  });
}
